import React, { useEffect, useMemo, useState } from 'react';
import './SchemaDoc.css';
import {
  CATEGORIES,
  TABLE_DOCS,
  describeColumn,
  describeTable,
} from './schemaDocs';

// Minimal CSV parser that handles quoted fields (e.g. "float(10,2)").
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else if (c === '\r') { /* skip */ }
      else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

function groupSchema(rows) {
  const header = rows[0];
  const idx = Object.fromEntries(header.map((h, i) => [h, i]));
  const tables = new Map();
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.length < 2 || !row[idx.TABLE_NAME]) continue;
    const name = row[idx.TABLE_NAME];
    if (!tables.has(name)) {
      tables.set(name, { name, columns: [] });
    }
    tables.get(name).columns.push({
      name: row[idx.COLUMN_NAME],
      ordinal: parseInt(row[idx.ORDINAL_POSITION], 10) || 0,
      type: row[idx.COLUMN_TYPE],
      nullable: row[idx.IS_NULLABLE] === 'YES',
      key: row[idx.COLUMN_KEY] || '',
      default: row[idx.COLUMN_DEFAULT] === 'NULL' ? null : row[idx.COLUMN_DEFAULT],
      extra: row[idx.EXTRA] || '',
    });
  }
  const list = Array.from(tables.values());
  list.forEach(t => t.columns.sort((a, b) => a.ordinal - b.ordinal));
  return list;
}

function matchesSearch(needle, table) {
  if (!needle) return true;
  if (table.name.toLowerCase().includes(needle)) return true;
  const doc = describeTable(table.name);
  if (doc?.displayName?.toLowerCase().includes(needle)) return true;
  if (doc?.description?.toLowerCase().includes(needle)) return true;
  return table.columns.some(c =>
    c.name.toLowerCase().includes(needle) ||
    describeColumn(table.name, c).toLowerCase().includes(needle)
  );
}

function TableCard({ table, highlight }) {
  const doc = describeTable(table.name);
  const pkCols = table.columns.filter(c => c.key === 'PRI').map(c => c.name);

  return (
    <section id={`table-${table.name}`} className="sd-table">
      <header className="sd-table-header">
        <div className="sd-table-title">
          <code className="sd-table-code">{table.name}</code>
          {doc?.displayName && <span className="sd-table-display">— {doc.displayName}</span>}
        </div>
        <div className="sd-table-meta">
          <span className="sd-meta-pill">{table.columns.length} columns</span>
          {pkCols.length > 0 && (
            <span className="sd-meta-pill sd-meta-pk" title={`Primary key: ${pkCols.join(', ')}`}>
              PK: {pkCols.join(', ')}
            </span>
          )}
        </div>
      </header>

      {doc?.description && <p className="sd-table-desc">{doc.description}</p>}
      {!doc && (
        <p className="sd-table-desc sd-table-desc-missing">
          No curated description yet — columns below show the raw schema only.
        </p>
      )}

      <div className="sd-columns-wrap">
        <table className="sd-columns">
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '48%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Column</th>
              <th>Type</th>
              <th>Null</th>
              <th>Key</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {table.columns.map(col => {
              const desc = describeColumn(table.name, col);
              const isHit = highlight && (
                col.name.toLowerCase().includes(highlight) ||
                desc.toLowerCase().includes(highlight)
              );
              return (
                <tr key={col.name} className={isHit ? 'sd-hit' : ''}>
                  <td className={`sd-col-name ${col.key === 'PRI' ? 'sd-pk' : ''}`}>
                    {col.name}
                  </td>
                  <td className="sd-col-type"><code>{col.type}</code></td>
                  <td className="sd-col-null">{col.nullable ? 'YES' : 'NO'}</td>
                  <td className="sd-col-key">
                    {col.key && <span className={`sd-key-pill sd-key-${col.key.toLowerCase()}`}>{col.key}</span>}
                  </td>
                  <td className="sd-col-desc">
                    {desc
                      ? <span>{desc}</span>
                      : <span className="sd-desc-missing">No description yet.</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SchemaDoc() {
  const [tables, setTables] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/docs/reporting-db-schema.csv', { cache: 'force-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const rows = parseCsv(text);
        const grouped = groupSchema(rows);
        if (!cancelled) setTables(grouped);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load schema');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const needle = search.trim().toLowerCase();

  // Build category → tables, preserving category ordering. Tables not listed
  // in any category (should be rare) end up in an "Uncategorized" bucket.
  const byCategory = useMemo(() => {
    if (!tables) return [];
    const map = new Map(tables.map(t => [t.name, t]));

    const used = new Set();
    const groups = CATEGORIES.map(cat => {
      const items = cat.tables
        .map(name => map.get(name))
        .filter(Boolean);
      items.forEach(t => used.add(t.name));
      return { ...cat, items };
    });

    const unlisted = tables
      .filter(t => !used.has(t.name))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (unlisted.length) {
      groups.push({
        id: 'other',
        title: 'Other',
        description: 'Tables present in the schema but not yet categorized.',
        items: unlisted,
      });
    }

    if (!needle) return groups;

    return groups
      .map(g => ({ ...g, items: g.items.filter(t => matchesSearch(needle, t)) }))
      .filter(g => g.items.length > 0);
  }, [tables, needle]);

  const totalColumns = useMemo(
    () => (tables || []).reduce((acc, t) => acc + t.columns.length, 0),
    [tables]
  );

  const documentedTables = useMemo(
    () => (tables || []).filter(t => TABLE_DOCS[t.name]).length,
    [tables]
  );

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="sd-page">
      <div className="sd-header">
        <div>
          <h1>VitalEngine Reporting DB Schema</h1>
          <p className="sd-subtitle">
            Read-only replica that VitalData pulls from nightly. Every table is prefixed{' '}
            <code>rpt_</code> and represents an anonymizable slice of production data. The
            sections below walk through the reporting tables by functional area, with a
            description of every column.
          </p>
        </div>
        {tables && (
          <div className="sd-stats">
            <div><strong>{tables.length}</strong> tables</div>
            <div><strong>{totalColumns}</strong> columns</div>
            <div><strong>{documentedTables}</strong> documented</div>
          </div>
        )}
      </div>

      <div className="sd-toolbar">
        <input
          type="text"
          className="sd-search"
          placeholder="Search tables, columns, or descriptions…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {needle && (
          <button className="sd-clear-btn" onClick={() => setSearch('')}>Clear</button>
        )}
      </div>

      {error && <div className="sd-error">Failed to load schema: {error}</div>}
      {!tables && !error && <div className="sd-loading">Loading schema…</div>}

      {tables && (
        <>
          <nav className="sd-category-nav">
            {byCategory.map(cat => (
              <button
                key={cat.id}
                type="button"
                className="sd-category-chip"
                onClick={() => scrollTo(`cat-${cat.id}`)}
              >
                {cat.title}
                <span className="sd-category-chip-count">{cat.items.length}</span>
              </button>
            ))}
          </nav>

          {byCategory.length === 0 && (
            <div className="sd-empty">No tables match "{search}".</div>
          )}

          {byCategory.map(cat => (
            <section key={cat.id} id={`cat-${cat.id}`} className="sd-category">
              <div className="sd-category-header">
                <h2>{cat.title}</h2>
                {cat.description && <p>{cat.description}</p>}
              </div>
              <div className="sd-category-tables">
                {cat.items.map(t => (
                  <TableCard key={t.name} table={t} highlight={needle} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export default SchemaDoc;
